import { motion } from 'framer-motion';
import { ArrowRight, Glasses } from 'lucide-react';

import { AppleMark } from '@/components/icons/AppleMark';
import { APP_STORE_URL } from '@/lib/constants';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1] as const,
    },
  },
};

const platformCards = [
  {
    name: 'iOS',
    description: 'App Store release',
    detail: 'Get Michelangelo on your iPhone today.',
    hasCta: true,
  },
  {
    name: 'Web',
    description: 'In active development',
    detail:
      "We're working to bring the experience to the web while keeping design and flow consistent.",
  },
  {
    name: 'Smart Glasses',
    description: 'Exploring Meta Glasses & beyond',
    detail:
      "New platforms deserve new UX. We're prototyping what creation looks like when it's always with you.",
    icon: Glasses,
  },
];

export function PlatformsSection() {
  return (
    <section id="platforms" className="py-24 lg:py-32 px-5 lg:px-12 bg-black scroll-mt-20">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          variants={containerVariants}
        >
          {/* Section Label */}
          <motion.p
            variants={itemVariants}
            className="text-xs font-medium text-white/40 uppercase tracking-[0.05em]"
          >
            Platforms
          </motion.p>

          {/* Headline */}
          <motion.h2
            variants={itemVariants}
            className="mt-4 text-3xl sm:text-4xl lg:text-[40px] font-semibold leading-[1.2] tracking-[-0.01em] text-white max-w-2xl"
          >
            Available on iOS. Expanding to new platforms.
          </motion.h2>

          {/* Description */}
          <motion.p
            variants={itemVariants}
            className="mt-6 text-base text-white/60 leading-relaxed max-w-2xl"
          >
            Michelangelo is already live on iPhone. We&apos;re continuing to build toward a broader
            future, including emerging platforms like smart glasses.
          </motion.p>

          {/* Cards Grid */}
          <motion.div variants={containerVariants} className="mt-12 grid md:grid-cols-3 gap-4">
            {platformCards.map((card) => (
              <motion.div
                key={card.name}
                variants={itemVariants}
                className="glass-card glass-card-hover p-6 flex flex-col glass-card-blur"
                style={{ willChange: 'transform' }}
              >
                <div className="flex items-center gap-2 mb-2">
                  {card.icon && <card.icon className="w-5 h-5 text-white/70" />}
                  <h3 className="text-base font-semibold text-white">{card.name}</h3>
                </div>
                <p className="text-sm text-white/50">{card.description}</p>
                <p className="text-sm text-white/40 mt-4 leading-relaxed flex-1">{card.detail}</p>
                {card.hasCta && (
                  <a
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-2 mt-4 bg-white text-black px-4 py-2 rounded-lg text-sm font-medium hover:bg-white/90 transition-all duration-200 hover:scale-[1.02]"
                  >
                    <AppleMark className="w-4 h-4" />
                    App Store
                    <ArrowRight className="w-4 h-4" />
                  </a>
                )}
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
